import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, GiftIcon, ArrowRightIcon } from 'lucide-react';
const PRICING_IMG = 'https://cdn.shopify.com/s/files/1/0995/0671/6970/files/3Pricing_Calculator_Ratio16_9.jpg?v=1777848014';
const CHECKLIST_IMG = 'https://cdn.shopify.com/s/files/1/0995/0671/6970/files/60-Day_Business_Launch_Checklist_16_9.jpg?v=1777861749';
const bonuses = [{
  tag: 'Bonus #1',
  img: PRICING_IMG,
  imgAlt: 'Pricing Profit Calculator',
  title: 'Pricing Profit Calculator',
  subtitle: 'Never undersell yourself again',
  description: 'Most sellers set prices by guessing. This calculator tells you the exact number. Enter your costs and target margin, get your minimum, ideal and premium price in seconds.',
  value: '$37',
  accentColor: '#00C9B1',
  accentGlow: 'rgba(0,201,177,0.18)',
  accentBorder: 'rgba(0,201,177,0.25)',
  accentBg: 'rgba(0,201,177,0.08)',
  highlights: ['Min, ideal and premium price calculated instantly', 'Auto-accounts for Shopify, Etsy and Amazon fees', 'Monthly profit projection by sales volume', 'Works for physical and digital products']
}, {
  tag: 'Bonus #2',
  img: CHECKLIST_IMG,
  imgAlt: '60-Day Business Launch Checklist',
  title: '60-Day Launch Checklist',
  subtitle: 'From zero to first sale in 60 days',
  description: 'A week-by-week action plan covering every critical step to launch or reorganize your store. Legal setup, listings, email flows, first ad, first review. Nothing left out.',
  value: '$27',
  accentColor: '#FFB800',
  accentGlow: 'rgba(255,184,0,0.18)',
  accentBorder: 'rgba(255,184,0,0.25)',
  accentBg: 'rgba(255,184,0,0.08)',
  highlights: ['60 tasks organized across 9 focused weeks', 'Covers legal, brand, products and first ad', 'PDF editable and print-ready version included', 'Works for Shopify, Etsy and any marketplace']
}];
export function BonusesSection() {
  return <section className="relative bg-[#060B18] py-24 overflow-hidden" data-id="element-22">
      <div className="absolute inset-0 pointer-events-none" data-id="element-23">
        <div className="absolute top-0 left-1/4 w-[700px] h-[500px] bg-teal/5 rounded-full blur-[150px]" data-id="element-24" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[400px] bg-[#FFB800]/5 rounded-full blur-[130px]" data-id="element-25" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-26">

        {/* Header — matching hero style */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-27">
          {/* Gift tag */}
          <motion.div initial={{
          scale: 0.85,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 18
        }} className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full mb-6" style={{
          background: 'linear-gradient(135deg, rgba(255,184,0,0.15), rgba(255,107,0,0.08))',
          border: '1.5px solid rgba(255,184,0,0.35)',
          boxShadow: '0 0 40px rgba(255,184,0,0.12)'
        }} data-id="element-28">
            <GiftIcon className="w-4 h-4 text-[#FFB800]" data-id="element-29" />
            <span className="text-[#FFB800] font-display font-bold text-sm tracking-wide" data-id="element-30">
              2 Bonuses Included Free With Every Order
            </span>
          </motion.div>

          {/* Big headline — "Unlock Extra $64 (in extras) for FREE" */}
          <div className="mb-4" data-id="element-31">
            <h2 className="font-display font-extrabold leading-none" data-id="element-32">
              {/* Line 1 */}
              <span className="block text-white text-4xl sm:text-5xl lg:text-6xl mb-2" data-id="element-33">
                Unlock Extra{' '}
                <span style={{
                background: 'linear-gradient(135deg, #FFB800, #FF6B00)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }} data-id="element-34">
                  $64
                </span>
              </span>
              {/* Line 2 — "in extras" muted + "for FREE" bright */}
              <span className="block text-4xl sm:text-5xl lg:text-6xl" data-id="element-35">
                <span className="text-white/30 line-through text-3xl sm:text-4xl lg:text-5xl mr-3" data-id="element-36">
                  in extras
                </span>
                <span style={{
                background: 'linear-gradient(135deg, #00C9B1, #0084A8)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }} data-id="element-37">
                  for FREE
                </span>
              </span>
            </h2>
          </div>

          <p className="text-white/45 text-base max-w-md mx-auto" data-id="element-38">
            Both bonuses are automatically added to your order. No codes. No opt-ins.
          </p>
        </motion.div>

        {/* Bonus cards */}
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8" data-id="element-39">
          {bonuses.map((bonus, i) => <motion.div key={bonus.title} initial={{
          opacity: 0,
          y: 32
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.18,
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} className="group flex flex-col rounded-3xl overflow-hidden cursor-default" style={{
          background: 'rgba(13, 26, 53, 0.9)',
          border: `1px solid ${bonus.accentBorder}`,
          boxShadow: `0 0 80px ${bonus.accentGlow}, 0 20px 60px rgba(0,0,0,0.5)`
        }} data-id="element-40">
              {/* Image — full width */}
              <div className="relative overflow-hidden" style={{
            aspectRatio: '16/9'
          }} data-id="element-41">
                <img src={bonus.img} alt={bonus.imgAlt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" data-id="element-42" />

                {/* Gradient overlay */}
                <div className="absolute inset-0" style={{
              background: `linear-gradient(to bottom,
                      rgba(6,11,24,0.08) 0%,
                      rgba(6,11,24,0.0) 35%,
                      rgba(13,26,53,0.5) 75%,
                      rgba(13,26,53,0.92) 100%)`
            }} data-id="element-43" />

                {/* Bonus tag — top left */}
                <div className="absolute top-4 left-4 z-10" data-id="element-44">
                  <span className="font-display font-extrabold text-xs uppercase tracking-widest px-4 py-2 rounded-full backdrop-blur-sm" style={{
                background: `${bonus.accentColor}22`,
                color: bonus.accentColor,
                border: `1.5px solid ${bonus.accentBorder}`
              }} data-id="element-45">
                    {bonus.tag} — FREE
                  </span>
                </div>

                {/* Value chip — top right */}
                <div className="absolute top-4 right-4 z-10 text-right backdrop-blur-sm px-3 py-1.5 rounded-xl" style={{
              background: 'rgba(6,11,24,0.55)',
              border: '1px solid rgba(255,255,255,0.08)'
            }} data-id="element-46">
                  <p className="text-white/40 text-xs line-through leading-none mb-0.5" data-id="element-47">
                    {bonus.value} value
                  </p>
                  <p className="font-display font-extrabold text-lg leading-none" style={{
                color: bonus.accentColor
              }} data-id="element-48">
                    FREE
                  </p>
                </div>

                {/*
                  Subtitle — positioned INSIDE the image at the very bottom,
                  with enough bottom padding so it clears the image edge cleanly.
                  Moved further down (pb-5) so it never overlaps image content.
                 */}
                <div className="absolute bottom-0 left-0 right-0 px-7 pb-5 z-10" data-id="element-49">
                  <p className="font-display font-bold text-sm uppercase tracking-widest" style={{
                color: bonus.accentColor
              }} data-id="element-50">
                    {bonus.subtitle}
                  </p>
                </div>
              </div>

              {/* Content */}
              <div className="px-7 pt-6 pb-8 flex flex-col flex-1" data-id="element-51">
                <h3 className="font-display font-extrabold text-2xl text-white mb-3 leading-snug" data-id="element-52">
                  {bonus.title}
                </h3>
                <p className="text-white/55 text-sm leading-relaxed mb-6" data-id="element-53">
                  {bonus.description}
                </p>

                <ul className="space-y-2.5 mb-6" data-id="element-54">
                  {bonus.highlights.map(h => <li key={h} className="flex items-start gap-3" data-id="element-55">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                  background: `${bonus.accentColor}18`,
                  border: `1px solid ${bonus.accentColor}35`
                }} data-id="element-56">
                        <CheckIcon className="w-3 h-3" style={{
                    color: bonus.accentColor
                  }} data-id="element-57" />
                      </span>
                      <span className="text-sm text-white/65 leading-snug" data-id="element-58">{h}</span>
                    </li>)}
                </ul>

                <div className="mt-auto flex items-center justify-between px-4 py-3 rounded-xl" style={{
              background: bonus.accentBg,
              border: `1px solid ${bonus.accentBorder}`
            }} data-id="element-59">
                  <span className="text-sm font-semibold" style={{
                color: bonus.accentColor
              }} data-id="element-60">
                    Included automatically with your order
                  </span>
                  <ArrowRightIcon className="w-4 h-4 flex-shrink-0" style={{
                color: bonus.accentColor
              }} data-id="element-61" />
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Bottom value callout */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.4
      }} className="mt-10 flex justify-center" data-id="element-62">
          <div className="flex items-center gap-6 px-8 py-4 rounded-2xl" style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)'
        }} data-id="element-63">
            <div data-id="element-64">
              <p className="text-white/30 text-xs uppercase tracking-widest mb-0.5" data-id="element-65">Combined value</p>
              <p className="text-white/28 text-xl font-bold line-through" data-id="element-66">$64</p>
            </div>
            <div className="w-px h-10 bg-white/10" data-id="element-67" />
            <div data-id="element-68">
              <p className="text-white/30 text-xs uppercase tracking-widest mb-0.5" data-id="element-69">Your price</p>
              <p className="text-2xl font-display font-extrabold" style={{
              background: 'linear-gradient(135deg, #00C9B1, #FFB800)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }} data-id="element-70">
                $0
              </p>
            </div>
            <div className="w-px h-10 bg-white/10" data-id="element-71" />
            <p className="text-white/50 text-sm max-w-[180px] leading-snug" data-id="element-72">
              Both bonuses added with every package, automatically.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}